/**
 * PerfilPage — Página de perfil compartilhada entre todos os portais
 * Acessível via /perfil de qualquer portal autenticado
 * Tabs: Datos personales | Cambiar contraseña
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth.store'
import { api } from '@/lib/api'
import { PageHeader } from '@/components/ui/PageHeader'
import { validarForcaSenha } from '@rutatec/shared-utils'
import type { SenhaForca } from '@rutatec/shared-utils'

// ── Types ─────────────────────────────────────────────────────

interface DisplayNameForm {
  displayName: string
}

interface ChangePasswordForm {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

// ── Força de senha visual ──────────────────────────────────────

const PasswordStrengthBar: React.FC<{ password: string }> = ({ password }) => {
  if (!password) return null
  const { forca, pontos } = validarForcaSenha(password)
  const colorMap: Record<SenhaForca, string> = {
    FRACA: 'bg-red',
    MEDIA: 'bg-amber',
    FORTE: 'bg-green',
    MUITO_FORTE: 'bg-navy',
  }
  const labelMap: Record<SenhaForca, string> = {
    FRACA: 'Débil',
    MEDIA: 'Media',
    FORTE: 'Fuerte',
    MUITO_FORTE: 'Muy fuerte',
  }
  const width = Math.min(100, pontos)

  return (
    <div className="mt-1">
      <div className="h-1.5 w-full rounded-full bg-gray-200">
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${colorMap[forca]}`}
          style={{ width: `${width}%` }}
        />
      </div>
      <p className="text-xs mt-0.5 text-gray-500">
        Contraseña: <span className={`font-medium text-${forca === 'FRACA' ? 'red' : forca === 'MEDIA' ? 'amber' : 'green'}-600`}>{labelMap[forca]}</span>
      </p>
    </div>
  )
}

// ── Tab: Datos Personales ──────────────────────────────────────

const DatosPersonalesTab: React.FC = () => {
  const { user, updateDisplayName } = useAuthStore()
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors } } = useForm<DisplayNameForm>({
    defaultValues: { displayName: user?.displayName ?? '' },
  })

  const mutation = useMutation({
    mutationFn: (data: DisplayNameForm) =>
      api.patch('/users/v1/users/me/display-name', data).then((r) => r.data),
    onSuccess: (_, variables) => {
      updateDisplayName(variables.displayName)
      setSuccessMsg('Nombre actualizado correctamente.')
      setTimeout(() => setSuccessMsg(null), 4000)
    },
  })

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6 max-w-lg">
      <div>
        <label className="block text-sm font-medium text-navy mb-1">
          Nombre de exhibición
        </label>
        <input
          {...register('displayName', {
            required: 'El nombre es requerido',
            minLength: { value: 2, message: 'Mínimo 2 caracteres' },
            maxLength: { value: 80, message: 'Máximo 80 caracteres' },
          })}
          className="input w-full"
          placeholder="Cómo se mostrará su nombre en el sistema"
        />
        {errors.displayName && (
          <p className="text-red text-xs mt-1">{errors.displayName.message}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Límite: 3 cambios por hora
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-navy mb-1">Email</label>
        <input
          value={user?.email ?? ''}
          disabled
          className="input w-full opacity-60 cursor-not-allowed"
        />
        <p className="text-xs text-gray-500 mt-1">El email no puede cambiarse desde aquí</p>
      </div>

      {successMsg && (
        <div className="rounded-lg bg-green/10 border border-green/30 p-3 text-green-700 text-sm">
          ✓ {successMsg}
        </div>
      )}
      {mutation.isError && (
        <div className="rounded-lg bg-red/10 border border-red/30 p-3 text-red text-sm">
          Error al actualizar. {(mutation.error as Error)?.message}
        </div>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        className="btn-primary px-6 py-2 disabled:opacity-50"
      >
        {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
      </button>
    </form>
  )
}

// ── Tab: Cambiar Contraseña ────────────────────────────────────

const CambiarContrasenaTab: React.FC = () => {
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [watchNew, setWatchNew] = useState('')

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<ChangePasswordForm>()

  const mutation = useMutation({
    mutationFn: (data: ChangePasswordForm) =>
      api.patch('/auth/change-password', data).then((r) => r.data),
    onSuccess: () => {
      setSuccessMsg('Contraseña actualizada. Por seguridad, inicie sesión nuevamente en sus otros dispositivos.')
      reset()
      setWatchNew('')
      setTimeout(() => setSuccessMsg(null), 6000)
    },
  })

  const newPassword = watch('newPassword', '')

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-5 max-w-lg">
      <div>
        <label className="block text-sm font-medium text-navy mb-1">
          Contraseña actual
        </label>
        <input
          type="password"
          {...register('currentPassword', { required: 'Requerida' })}
          className="input w-full"
          autoComplete="current-password"
        />
        {errors.currentPassword && (
          <p className="text-red text-xs mt-1">{errors.currentPassword.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-navy mb-1">
          Nueva contraseña
        </label>
        <input
          type="password"
          {...register('newPassword', {
            required: 'Requerida',
            minLength: { value: 8, message: 'Mínimo 8 caracteres' },
            pattern: {
              value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])/,
              message: 'Debe tener mayúscula, minúscula y número',
            },
            onChange: (e) => setWatchNew(e.target.value),
          })}
          className="input w-full"
          autoComplete="new-password"
        />
        <PasswordStrengthBar password={newPassword || watchNew} />
        {errors.newPassword && (
          <p className="text-red text-xs mt-1">{errors.newPassword.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-navy mb-1">
          Confirmar nueva contraseña
        </label>
        <input
          type="password"
          {...register('confirmPassword', {
            required: 'Requerida',
            validate: (v) => v === watch('newPassword') || 'Las contraseñas no coinciden',
          })}
          className="input w-full"
          autoComplete="new-password"
        />
        {errors.confirmPassword && (
          <p className="text-red text-xs mt-1">{errors.confirmPassword.message}</p>
        )}
      </div>

      {successMsg && (
        <div className="rounded-lg bg-green/10 border border-green/30 p-3 text-green-700 text-sm">
          ✓ {successMsg}
        </div>
      )}
      {mutation.isError && (
        <div className="rounded-lg bg-red/10 border border-red/30 p-3 text-red text-sm">
          {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message
            ?? 'Error al cambiar la contraseña'}
        </div>
      )}

      <button
        type="submit"
        disabled={mutation.isPending}
        className="btn-primary px-6 py-2 disabled:opacity-50"
      >
        {mutation.isPending ? 'Procesando…' : 'Cambiar contraseña'}
      </button>
    </form>
  )
}

// ── Página principal ───────────────────────────────────────────

export const PerfilPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'password' ? 'password' : 'datos'
  const { user, getDisplayName } = useAuthStore()

  return (
    <div className="space-y-6">
      <PageHeader
        title="Mi perfil"
        subtitle={`${getDisplayName()} · ${user?.role}`}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {(['datos', 'password'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setSearchParams(t === 'datos' ? {} : { tab: t })}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t
                  ? 'border-navy text-navy'
                  : 'border-transparent text-gray-500 hover:text-navy'
              }`}
            >
              {t === 'datos' ? '👤 Datos personales' : '🔑 Cambiar contraseña'}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenido */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        {tab === 'datos' ? <DatosPersonalesTab /> : <CambiarContrasenaTab />}
      </div>
    </div>
  )
}
